/**
 * ClickHandler - Click detection for structure and KEB selection
 *
 * Handles click events on the map to detect structure and KEB selections
 * and emits events for UI components to respond to.
 */

/**
 * ClickHandler class - manages structure and KEB click detection
 */
export class ClickHandler {
  /**
   * Create a new ClickHandler
   * @param {import('./CellMap.js').CellMap} cellMap - The CellMap instance
   * @param {import('./StructureLayer.js').StructureLayer} structureLayer - The StructureLayer instance
   * @param {import('./KebLayer.js').KebLayer} [kebLayer] - Optional KebLayer instance
   */
  constructor(cellMap, structureLayer, kebLayer = null) {
    this.cellMap = cellMap;
    this.map = cellMap.getMap();
    this.structureLayer = structureLayer;
    this.kebLayer = kebLayer;
    this.selectedStructure = null;
    this.listeners = new Set();
    this.kebListeners = new Set();

    this.setupClickHandler();
    this.setupHoverHandler();
  }

  /**
   * Set up click detection
   * @private
   */
  setupClickHandler() {
    this.map.on('singleclick', (event) => {
      // Check for KEB first (they're on top)
      if (this.kebLayer) {
        const kebFeature = this.map.forEachFeatureAtPixel(
          event.pixel,
          (feature) => feature,
          {
            layerFilter: (layer) => layer === this.kebLayer.getLayer()
          }
        );

        if (kebFeature && kebFeature.get('featureType') === 'keb') {
          this.selectKeb(kebFeature, event.coordinate);
          return;
        }
      }

      // Check for structure
      const structureFeature = this.map.forEachFeatureAtPixel(
        event.pixel,
        (feature) => feature,
        {
          layerFilter: (layer) => layer === this.structureLayer.getLayer()
        }
      );

      if (structureFeature) {
        this.selectStructure(structureFeature);
      } else {
        this.clearSelection();
      }
    });
  }

  /**
   * Set up hover cursor changes
   * @private
   */
  setupHoverHandler() {
    this.map.on('pointermove', (event) => {
      // Don't change cursor while dragging
      if (event.dragging) {
        return;
      }

      // Check for KEB hover
      let hit = false;
      if (this.kebLayer) {
        hit = this.map.hasFeatureAtPixel(event.pixel, {
          layerFilter: (layer) => layer === this.kebLayer.getLayer()
        });
      }

      // If no KEB, check for structure
      if (!hit) {
        hit = this.map.hasFeatureAtPixel(event.pixel, {
          layerFilter: (layer) => layer === this.structureLayer.getLayer()
        });
      }

      this.map.getTargetElement().style.cursor = hit ? 'pointer' : '';
    });
  }

  /**
   * Set the KEB layer (can be set after construction)
   * @param {import('./KebLayer.js').KebLayer} kebLayer - The KebLayer instance
   */
  setKebLayer(kebLayer) {
    this.kebLayer = kebLayer;
  }

  /**
   * Handle KEB selection
   * @param {import('ol/Feature').default} feature - OpenLayers feature
   * @param {Array} coordinate - [x, y] click coordinate
   */
  selectKeb(feature, coordinate) {
    const kebId = feature.get('kebId');
    const keb = this.kebLayer.getKebById(kebId);

    if (keb) {
      this.emitKeb('keb-select', { keb, coordinate });
    }
  }

  /**
   * Subscribe to KEB events
   * @param {string} event - Event name ('keb-select')
   * @param {Function} callback - Event handler
   * @returns {Function} Unsubscribe function
   */
  onKeb(event, callback) {
    const listener = { event, callback };
    this.kebListeners.add(listener);
    return () => this.kebListeners.delete(listener);
  }

  /**
   * Emit a KEB event
   * @param {string} event - Event name
   * @param {*} data - Event data
   * @private
   */
  emitKeb(event, data) {
    this.kebListeners.forEach((listener) => {
      if (listener.event === event) {
        try {
          listener.callback(data);
        } catch (error) {
          console.error(`ClickHandler: Error in ${event} listener:`, error);
        }
      }
    });
  }

  /**
   * Select a structure
   * @param {import('ol/Feature').default} feature - OpenLayers feature
   */
  selectStructure(feature) {
    const structureId = feature.get('structureId');
    const structure = this.structureLayer.getStructureById(structureId);

    if (!structure) {
      console.warn('ClickHandler: Structure not found:', structureId);
      return;
    }

    // Build ancestor tree for breadcrumbs
    const ancestorTree = this.structureLayer.getAncestorTree(structureId);

    this.selectedStructure = {
      ...structure,
      ancestorTree: ancestorTree.map(s => ({
        id: s.id,
        name: s.name
      }))
    };

    // Emit event
    this.emit('structure-select', this.selectedStructure);
  }

  /**
   * Clear current selection
   */
  clearSelection() {
    if (this.selectedStructure) {
      this.selectedStructure = null;
      this.emit('structure-deselect', null);
    }
  }

  /**
   * Get currently selected structure
   * @returns {Object|null}
   */
  getSelectedStructure() {
    return this.selectedStructure;
  }

  /**
   * Programmatically select a structure by ID
   * @param {number} structureId - Structure ID to select
   */
  selectById(structureId) {
    const structure = this.structureLayer.getStructureById(structureId);
    if (structure) {
      // Create a mock feature with the required properties
      const mockFeature = {
        get: (key) => {
          if (key === 'structureId') {
            return structureId;
          }
          return structure[key];
        }
      };
      this.selectStructure(mockFeature);
    } else {
      console.warn('ClickHandler: Cannot select - structure not found:', structureId);
    }
  }

  // Simple event emitter

  /**
   * Subscribe to events
   * @param {string} event - Event name ('structure-select' or 'structure-deselect')
   * @param {Function} callback - Event handler
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    const listener = { event, callback };
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Emit an event
   * @param {string} event - Event name
   * @param {*} data - Event data
   * @private
   */
  emit(event, data) {
    this.listeners.forEach(listener => {
      if (listener.event === event) {
        try {
          listener.callback(data);
        } catch (error) {
          console.error(`ClickHandler: Error in ${event} listener:`, error);
        }
      }
    });
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners() {
    this.listeners.clear();
    this.kebListeners.clear();
  }
}
